import React, { useState, useEffect, useRef } from 'react'
import { useLocation, useParams, Link, useNavigate } from 'react-router-dom';
import Footer from './component/Footer';
import api from './api';
import { useCart } from './context/CartContext';
import { FaHeart, FaRegHeart } from "react-icons/fa";
import { fetchSections } from './services/userProfile';
import { useWishlist } from './context/WishlistContext';
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";

const ProductCategoryList = () => {

    const [wishlist, setWishlist] = useState([]);
    const [wishlistedProducts, setWishlistedProducts] = useState({});
    const { wishlistCount, setWishlistCount } = useWishlist();

    const category = useLocation();
    const { sections, setSections } = category.state || {};
    const [catname, setCategoryn] = useState([]);

    const navigate = useNavigate();
    const { addToCart, updateQuantity, removeFromCart, cart } = useCart();
    const { categoryId } = useParams();
    const [products, setProducts] = useState([]);
    const [loading, setLoading] = useState(true);
    const [isVisible, setIsVisible] = useState(false);

    // ─── Per-product cart quantities (keyed by product._id) ──────────────────
    // Initialised SYNCHRONOUSLY from localStorage so on hard-refresh the
    // correct buttons are shown immediately — before any async cart load.
    const [productQuantities, setProductQuantities] = useState(() => {
        try {
            const saved = localStorage.getItem("cartQuantities");
            if (saved) {
                const parsed = JSON.parse(saved);
                if (parsed && typeof parsed === "object") return parsed;
            }
        } catch (e) { /* ignore */ }
        return {};
    });

    // Stable ref so async handlers never close over stale state
    const productQuantitiesRef = useRef(productQuantities);

    // Every write goes through here → ref + localStorage always in sync
    const updateProductQty = (updater) => {
        setProductQuantities((prev) => {
            const next = typeof updater === "function" ? updater(prev) : updater;
            productQuantitiesRef.current = next;
            try { localStorage.setItem("cartQuantities", JSON.stringify(next)); } catch (e) { }
            return next;
        });
    };

    // Seed from cart context — reconcile localStorage with live cart
    useEffect(() => {
        if (cart.length === 0) return;
        updateProductQty((prev) => {
            const next = { ...prev };
            const cartIdSet = new Set(cart.map((i) => i.id || i._id || i.productId));
            // Remove stale keys
            Object.keys(next).forEach((id) => {
                if (!cartIdSet.has(id)) delete next[id];
            });
            // Add missing keys from cart
            cart.forEach((item) => {
                const id = item.id || item._id || item.productId;
                if (id && next[id] === undefined) {
                    next[id] = item.quantity || 1;
                }
            });
            return next;
        });
    }, [cart]);
    // ─────────────────────────────────────────────────────────────────────────

    // ─── Wishlist — fetch on mount so heart icons are correct ────────────────
    useEffect(() => {
        const fetchWishlist = async () => {
            const storedData = localStorage.getItem("userData");
            const token = storedData ? JSON.parse(storedData).token : null;
            if (!token) return;
            try {
                const res = await api.get("/user/get-wishlists", {
                    headers: { Authorization: `Bearer ${token}` },
                });
                const items =
                    res.data?.data ||
                    res.data?.wishlist ||
                    res.data?.products ||
                    [];
                if (Array.isArray(items)) {
                    const map = {};
                    items.forEach((item) => {
                        const id = item._id || item?.product?._id;
                        if (id) map[id] = true;
                    });
                    setWishlistedProducts(map);
                    setWishlist(items);
                    setWishlistCount(items.length);
                }
            } catch (err) {
                console.log("Failed to fetch wishlist", err);
            }
        };
        fetchWishlist();
    }, []);
    // ─────────────────────────────────────────────────────────────────────────

    const goToProductDetails = (product) => {
        navigate(`/product-details/${product._id}`, { state: { items: product } });
    };

    // ─── Fetch Products by Category ───────────────────────────────────────────
    useEffect(() => {
        if (!categoryId) return;
        const fetchProducts = async () => {
            try {
                setLoading(true);
                const res = await api.get(`/user/getProductsByCategory/${categoryId}`);
                setProducts(res.data.products || []);
            } catch (err) {
                console.error("Failed to fetch products:", err);
            } finally {
                setLoading(false);
            }
        };
        fetchProducts();
    }, [categoryId]);

    useEffect(() => {
        const handleScroll = () => setIsVisible(window.scrollY > 300);
        window.addEventListener('scroll', handleScroll);
        return () => window.removeEventListener('scroll', handleScroll);
    }, []);

    const handleScrollTop = () => window.scrollTo({ top: 0, behavior: 'smooth' });

    useEffect(() => {
        const getCategoryName = async () => {
            try {
                const res = await fetchSections();
                setCategoryn(res);
            } catch (error) {
                console.log("error", error.message);
            }
        };
        getCategoryName();
    }, []);
    // ─────────────────────────────────────────────────────────────────────────

    // ─── Wishlist toggle ──────────────────────────────────────────────────────
    const addWishList = async (product) => {
        const storedData = localStorage.getItem("userData");
        const token = storedData ? JSON.parse(storedData).token : null;
        if (!token) {
            setWishlist([]); setWishlistCount(0); setWishlistedProducts({});
            navigate("/user-login"); return;
        }
        const isWishlisted = !!wishlistedProducts[product._id];
        try {
            await api.put(
                "/user/add-wishlist",
                { productId: product._id, product },
                { headers: { Authorization: `Bearer ${token}` } }
            );
            setWishlistedProducts((prev) => ({ ...prev, [product._id]: !isWishlisted }));
            setWishlistCount((prev) => !isWishlisted ? prev + 1 : Math.max(prev - 1, 0));
            if (isWishlisted) {
                setWishlist((prev) => prev.filter((item) => item._id !== product._id));
                toast.info("Removed from wishlist", { position: "top-right", autoClose: 2000, theme: "dark" });
            } else {
                setWishlist((prev) => [...prev, product]);
                toast.success("Added to wishlist ❤️", { position: "top-right", autoClose: 2000, theme: "dark" });
            }
        } catch (err) {
            console.error("Error updating wishlist:", err);
            toast.error("Failed to update wishlist. Please try again.", { position: "top-right", autoClose: 3000, theme: "dark" });
        }
    };
    // ─────────────────────────────────────────────────────────────────────────

    // ─── Add to Cart ──────────────────────────────────────────────────────────
    const handleAddToCart = async (product) => {
        if (!product) { alert("Product not found!"); return; }
        const storedData = localStorage.getItem("userData");
        const token = storedData ? JSON.parse(storedData).token : null;
        if (!token) {
            setWishlist([]); setWishlistCount(0); setWishlistedProducts({});
            navigate("/user-login"); return;
        }
        const selectedColor = product?.variants?.[0]?.color?.name;
        const selectedSize = product?.variants?.[0]?.sizes?.[0]?.size;
        const selectedStock = product?.variants?.[0]?.sizes?.[0]?.stock;

        if (selectedStock <= 0) {
            toast.warning("This Size is out of stock. Please Select another Size", {
                position: "top-right",
                autoClose: 2000,
                theme: "dark",
            });
            return;
        }

        await addToCart(product, selectedColor, selectedSize, "1");

        // Set ONLY this product's qty to 1 — never touch other products
        updateProductQty((prev) => ({ ...prev, [product._id]: 1 }));

        toast.success("Added to cart successfully", {
            position: "top-right", autoClose: 3000,
            hideProgressBar: false, closeOnClick: true,
            pauseOnHover: true, draggable: true, theme: "dark",
        });
    };
    // ─────────────────────────────────────────────────────────────────────────

    // ─── Increase quantity ────────────────────────────────────────────────────
    const handleIncreaseQuantity = async (product) => {
        const storedData = localStorage.getItem("userData");
        const token = storedData ? JSON.parse(storedData).token : null;
        if (!token) { navigate("/user-login"); return; }

        const currentQty = productQuantitiesRef.current[product._id] || 1;
        const maxStock = product?.variants?.[0]?.sizes?.[0]?.stock || 99;
        if (currentQty >= maxStock) {
            toast.warning("Maximum stock limit reached", { position: "top-right", autoClose: 2000, theme: "dark" });
            return;
        }
        const newQty = currentQty + 1;
        updateProductQty((prev) => ({ ...prev, [product._id]: newQty }));
        await updateQuantity(
            product,
            product?.variants?.[0]?.color?.name,
            product?.variants?.[0]?.sizes?.[0]?.size,
            newQty
        );
    };
    // ─────────────────────────────────────────────────────────────────────────

    // ─── Decrease quantity ────────────────────────────────────────────────────
    const handleDecreaseQuantity = async (product) => {
        const storedData = localStorage.getItem("userData");
        const token = storedData ? JSON.parse(storedData).token : null;
        if (!token) { navigate("/user-login"); return; }

        const currentQty = productQuantitiesRef.current[product._id] || 1;
        const newQty = currentQty - 1;

        if (newQty <= 0) {
            const cartItem = cart.find((item) =>
                (item.id || item._id || item.productId) === product._id
            );
            if (cartItem) await removeFromCart(cartItem.id || cartItem._id);
            // Remove ONLY this product's key
            updateProductQty((prev) => {
                const updated = { ...prev };
                delete updated[product._id];
                return updated;
            });
            return;
        }
        updateProductQty((prev) => ({ ...prev, [product._id]: newQty }));
        await updateQuantity(
            product,
            product?.variants?.[0]?.color?.name,
            product?.variants?.[0]?.sizes?.[0]?.size,
            newQty
        );
    };
    // ─────────────────────────────────────────────────────────────────────────

    // ─── Buy Now ──────────────────────────────────────────────────────────────
    const handleBuyNow = (product) => {
        const storedData = localStorage.getItem("userData");
        const token = storedData ? JSON.parse(storedData).token : null;

        const selectedColor = product?.variants?.[0]?.color?.name;
        const selectedSize = product?.variants?.[0]?.sizes?.[0]?.size;
        const quantity = "1";

        const selectedStock = product?.variants?.[0]?.sizes?.[0]?.stock;

        if (selectedStock <= 0) {
            toast.warning("This Size is out of stock. Please Select another Size", {
                position: "top-right",
                autoClose: 2000,
                theme: "dark",
            });
            return;
        }

        const selectedVariant = getSelectedVariant(product, selectedColor, selectedSize);
        if (!selectedVariant) {
            console.error("Selected variant not found!");
            return;
        }

        const updatedProduct = {
            ...product,
            variant: selectedVariant,
            quantity: quantity,
        };

        if (!token) {
            setWishlist([]); setWishlistCount(0); setWishlistedProducts({});
            navigate("/user-login"); return;
        }

        navigate("/check-out", {
            state: {
                token,
                products: updatedProduct,
                singleProduct: "yes",
                singleProductId: product._id,
            },
        });
    };
    // ─────────────────────────────────────────────────────────────────────────

    const truncateDescription = (text, wordLimit) => {
        if (!text) return "";
        const words = text.split(" ");
        return words.length <= wordLimit ? text : words.slice(0, wordLimit).join(" ") + "...";
    };

    const getSelectedVariant = (product, selectedColor, selectedSize) => {

        if (!product?.variants) return null;

        const colorVariant = product.variants.find(
            (v) => v.color.name === selectedColor
        );

        if (!colorVariant) return null;

        const sizeVariant = colorVariant.sizes.find(
            (s) => s.size === selectedSize
        );
        if (!sizeVariant) return null;

        return {
            color: colorVariant.color,
            size: sizeVariant.size,
            originalPrice: sizeVariant.originalPrice,
            discountedPrice: sizeVariant.discountedPrice,
            stock: sizeVariant.stock,
            images: colorVariant.images,
        };
    };

    return (
        <>
            <ToastContainer />

          
            <div className="main__content_wrapper container py-4 container py-4">
                <h2 className="mb-4">
                    {cart[0]?.category?.name ? cart[0]?.category?.name : "Category Products"}
                </h2>

                <div className="row">
                    {loading ? (
                        <div className="col-12 text-center py-5">
                            <div className="spinner-border text-primary" role="status">
                                <span className="visually-hidden">Loading...</span>
                            </div>
                        </div>
                    ) : products.length > 0 ? (
                        <div className="row">
                            {products.map((product) => {
                                const allOutOfStock = product.variants?.every((variant) =>
                                    variant.sizes?.every((size) => size.stock === 0)
                                );

                                const selectedStockProduct = product?.variants?.[0]?.sizes?.[0]?.stock;

                                // ── isInCart checks ONLY this product's _id ──
                                const isInCart = Object.prototype.hasOwnProperty.call(
                                    productQuantities, product._id
                                );
                                const qty = productQuantities[product._id] || 0;
                                const isWishlisted = !!wishlistedProducts[product._id];

                                return (
                                    <div
                                        className="col-md-3 col-sm-6 mb-4 d-flex align-items-stretch"
                                        key={product._id}
                                    >
                                        <div
                                            style={{
                                                border: "3px solid #FFD700",
                                                borderRadius: "6px",
                                                overflow: "hidden",
                                                background: "#fff",
                                                boxShadow: "0 2px 6px rgba(0,0,0,0.1)",
                                                transition: "transform 0.3s ease, box-shadow 0.3s ease",
                                                display: "flex",
                                                flexDirection: "column",
                                                width: "100%",
                                                minHeight: "370px",
                                            }}
                                            onMouseEnter={(e) => {
                                                e.currentTarget.style.transform = "translateY(-8px)";
                                                e.currentTarget.style.boxShadow = "0 8px 20px rgba(0,0,0,0.2)";
                                            }}
                                            onMouseLeave={(e) => {
                                                e.currentTarget.style.transform = "translateY(0)";
                                                e.currentTarget.style.boxShadow = "0 2px 6px rgba(0,0,0,0.1)";
                                            }}
                                        >
                                            {/* ── Image ── */}
                                            <div
                                                style={{
                                                    position: "relative",
                                                    width: "100%",
                                                    height: "220px",
                                                    overflow: "hidden",
                                                    cursor: "pointer",
                                                }}
                                                onClick={() => goToProductDetails(product)}
                                            >
                                                <img
                                                    src={
                                                        product.variants?.[0]?.images?.[0] ||
                                                        product.firstVariant?.image ||
                                                        "https://via.placeholder.com/300x300?text=No+Image"
                                                    }
                                                    alt={product.name}
                                                    style={{
                                                        width: "100%", height: "100%",
                                                        objectFit: "cover", objectPosition: "center",
                                                        display: "block",
                                                    }}
                                                />

                                                {allOutOfStock && (
                                                    <span style={{
                                                        position: "absolute", top: "10px", left: "10px",
                                                        background: "rgba(255,255,255,0.9)", color: "red",
                                                        fontWeight: "bold", fontSize: "12px",
                                                        padding: "3px 8px", borderRadius: "3px",
                                                    }}>
                                                        Out of Stock
                                                    </span>
                                                )}

                                                {/* Wishlist button */}
                                                <button
                                                    onClick={(e) => { e.stopPropagation(); addWishList(product); }}
                                                    style={{
                                                        position: "absolute", top: "10px", right: "10px",
                                                        background: isWishlisted ? "rgba(237,46,46,0.95)" : "#CEB256",
                                                        border: "none", borderRadius: "50%",
                                                        width: "32px", height: "32px",
                                                        display: "flex", justifyContent: "center", alignItems: "center",
                                                        cursor: "pointer",
                                                        boxShadow: "0 2px 6px rgba(0,0,0,0.15)",
                                                        transition: "all 0.3s",
                                                    }}
                                                >
                                                    {isWishlisted
                                                        ? <FaHeart style={{ color: "white", fontSize: "16px" }} />
                                                        : <FaRegHeart style={{ color: "white", fontSize: "16px" }} />
                                                    }
                                                </button>

                                                {/* Rating badge */}
                                                <div style={{
                                                    position: "absolute", bottom: "8px", left: "8px",
                                                    background: "rgba(0,0,0,0.6)", color: "white",
                                                    padding: "3px 6px", borderRadius: "4px", fontSize: "11px",
                                                    display: "flex", alignItems: "center", gap: "3px",
                                                }}>
                                                    ⭐ {product.averageRating?.toFixed(1) || "0.0"}
                                                </div>
                                            </div>

                                            {/* ── Card body ── */}
                                            <div style={{
                                                flex: 1, display: "flex", flexDirection: "column",
                                                padding: "10px 8px",
                                            }}>
                                                <div style={{ flex: 1, marginBottom: "8px" }}>
                                                    <span style={{
                                                        display: "block", fontSize: "14px", fontWeight: "700",
                                                        color: "#333", marginBottom: "4px", lineHeight: "1.3",
                                                    }}>
                                                        {product.name || product.title}
                                                    </span>
                                                    <span
                                                        style={{
                                                            display: "block",
                                                            fontSize: "14px",
                                                        }}
                                                    >Size : {product.variants?.[0]?.sizes?.[0]?.size}, Color : {product.variants?.[0]?.color?.name}, Stock : {product.variants?.[0]?.sizes?.[0]?.stock}</span>
                                                    <p style={{
                                                        fontSize: "11px", color: "#666",
                                                        marginBottom: "6px", lineHeight: "1.4",
                                                    }}>
                                                        {truncateDescription(product.description, 6)}
                                                    </p>

                                                    {/* Price */}
                                                    <div style={{
                                                        display: "flex", alignItems: "center",
                                                        flexWrap: "wrap", gap: "5px", marginBottom: "8px",
                                                    }}>
                                                        {product.variants?.[0]?.sizes?.[0]?.discountedPrice ? (
                                                            <>
                                                                <span style={{ color: "#28a745", fontWeight: "700", fontSize: "15px" }}>
                                                                    ₹{Number(product.variants[0].sizes[0].discountedPrice)}
                                                                </span>
                                                                <span style={{ color: "#999", fontSize: "13px", textDecoration: "line-through" }}>
                                                                    ₹{product.variants[0].sizes[0].originalPrice || 0}
                                                                </span>
                                                                {product.variants[0].sizes[0].discount && (
                                                                    <span style={{
                                                                        color: "black", padding: "2px 6px",
                                                                        borderRadius: "3px", fontSize: "10px", fontWeight: "600",
                                                                    }}>
                                                                        {product.variants[0].sizes[0].discount}% OFF
                                                                    </span>
                                                                )}
                                                            </>
                                                        ) : (
                                                            <span style={{ color: "#333", fontWeight: "700", fontSize: "15px" }}>
                                                                ₹{product.variants?.[0]?.sizes?.[0]?.originalPrice ||
                                                                    product.firstVariant?.originalPrice || 0}
                                                            </span>
                                                        )}
                                                    </div>
                                                </div>

                                                {/* ── Buttons ── */}
                                                <div style={{ marginTop: "auto", paddingTop: "8px" }}>
                                                    {isInCart && !allOutOfStock && (selectedStockProduct > 0)  ? (
                                                        /* Quantity controls + Buy Now */
                                                        <div style={{
                                                            display: "flex", flexDirection: "row",
                                                            gap: "8px", width: "100%", alignItems: "center",
                                                        }}>
                                                            {/* Styled qty control */}
                                                            <div style={{
                                                                flex: 1,
                                                                display: "flex",
                                                                alignItems: "center",
                                                                border: "2px solid #CEB256",
                                                                borderRadius: "6px",
                                                                overflow: "hidden",
                                                                height: "36px",
                                                            }}>
                                                                <button
                                                                    onClick={(e) => {
                                                                        e.stopPropagation();
                                                                        handleDecreaseQuantity(product);
                                                                    }}
                                                                    style={{
                                                                        width: "36px",
                                                                        height: "100%",
                                                                        background: "#CEB256",
                                                                        color: "#fff",
                                                                        border: "none",
                                                                        fontSize: "20px",
                                                                        fontWeight: "700",
                                                                        cursor: "pointer",
                                                                        display: "flex",
                                                                        alignItems: "center",
                                                                        justifyContent: "center",
                                                                        flexShrink: 0,
                                                                        transition: "background 0.2s",
                                                                        lineHeight: 1,
                                                                        paddingBottom: "2px",
                                                                    }}
                                                                    onMouseEnter={(e) => e.currentTarget.style.background = "#b89a3e"}
                                                                    onMouseLeave={(e) => e.currentTarget.style.background = "#CEB256"}
                                                                >
                                                                    −
                                                                </button>
                                                                <span style={{
                                                                    flex: 1,
                                                                    textAlign: "center",
                                                                    fontSize: "14px",
                                                                    fontWeight: "700",
                                                                    color: "#333",
                                                                    userSelect: "none",
                                                                }}>
                                                                    {qty}
                                                                </span>
                                                                <button
                                                                    onClick={(e) => {
                                                                        e.stopPropagation();
                                                                        handleIncreaseQuantity(product);
                                                                    }}
                                                                    style={{
                                                                        width: "36px",
                                                                        height: "100%",
                                                                        background: "#CEB256",
                                                                        color: "#fff",
                                                                        border: "none",
                                                                        fontSize: "20px",
                                                                        fontWeight: "700",
                                                                        cursor: "pointer",
                                                                        display: "flex",
                                                                        alignItems: "center",
                                                                        justifyContent: "center",
                                                                        flexShrink: 0,
                                                                        transition: "background 0.2s",
                                                                        lineHeight: 1,
                                                                    }}
                                                                    onMouseEnter={(e) => e.currentTarget.style.background = "#b89a3e"}
                                                                    onMouseLeave={(e) => e.currentTarget.style.background = "#CEB256"}
                                                                >
                                                                    +
                                                                </button>
                                                            </div>

                                                            {/* Buy Now */}
                                                            <button
                                                                onClick={(e) => { e.stopPropagation(); handleBuyNow(product); }}
                                                                style={{
                                                                    flex: 1, height: "36px",
                                                                    backgroundColor: "#28a745",
                                                                    color: "#fff", border: "none",
                                                                    borderRadius: "6px",
                                                                    fontSize: "12px", fontWeight: "700",
                                                                    cursor: "pointer", whiteSpace: "nowrap",
                                                                    transition: "background 0.2s",
                                                                }}
                                                                onMouseEnter={(e) => e.currentTarget.style.background = "#1e7e34"}
                                                                onMouseLeave={(e) => e.currentTarget.style.background = "#28a745"}
                                                            >
                                                                Buy Now
                                                            </button>
                                                        </div>
                                                    ) : (
                                                        /* Add to Cart + Buy Now */
                                                        <div style={{ display: "flex", gap: "6px", width: "100%" }}>
                                                            <button
                                                                onClick={(e) => { e.stopPropagation(); handleAddToCart(product); }}
                                                                disabled={allOutOfStock}
                                                                style={{
                                                                    flex: 1, height: "36px",
                                                                    backgroundColor: allOutOfStock ? "#ccc" : "#CEB256",
                                                                    color: "white", border: "none",
                                                                    borderRadius: "6px",
                                                                    fontSize: "11px", fontWeight: "700",
                                                                    cursor: allOutOfStock ? "not-allowed" : "pointer",
                                                                    transition: "background 0.2s",
                                                                    whiteSpace: "nowrap",
                                                                }}
                                                                onMouseEnter={(e) => { if (!allOutOfStock) e.currentTarget.style.background = "#b89a3e"; }}
                                                                onMouseLeave={(e) => { if (!allOutOfStock) e.currentTarget.style.background = "#CEB256"; }}
                                                            >
                                                                Add to Cart
                                                            </button>
                                                            <button
                                                                onClick={(e) => { e.stopPropagation(); handleBuyNow(product); }}
                                                                disabled={allOutOfStock}
                                                                style={{
                                                                    flex: 1, height: "36px",
                                                                    backgroundColor: allOutOfStock ? "#ccc" : "#28a745",
                                                                    color: "white", border: "none",
                                                                    borderRadius: "6px",
                                                                    fontSize: "11px", fontWeight: "700",
                                                                    cursor: allOutOfStock ? "not-allowed" : "pointer",
                                                                    transition: "background 0.2s",
                                                                    whiteSpace: "nowrap",
                                                                }}
                                                                onMouseEnter={(e) => { if (!allOutOfStock) e.currentTarget.style.background = "#1e7e34"; }}
                                                                onMouseLeave={(e) => { if (!allOutOfStock) e.currentTarget.style.background = "#28a745"; }}
                                                            >
                                                                Buy Now
                                                            </button>
                                                        </div>
                                                    )}
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                );
                            })}
                        </div>
                    ) : (
                        <p>No products found for this category.</p>
                    )}
                </div>
            </div>

            <Footer />

            {/* Scroll to top */}
            <button
                onClick={handleScrollTop}
                style={{
                    position: 'fixed', bottom: '30px', right: '30px',
                    width: '50px', height: '50px', borderRadius: '50%',
                    backgroundColor: '#ceb256', color: '#fff', border: 'none',
                    zIndex: 1000, cursor: 'pointer',
                    display: isVisible ? 'flex' : 'none',
                    alignItems: 'center', justifyContent: 'center',
                    boxShadow: '0 4px 10px rgba(0,0,0,0.2)',
                }}
            >
                <svg xmlns="http://www.w3.org/2000/svg" height="24" width="24" viewBox="0 0 512 512"
                    fill="none" stroke="currentColor" strokeWidth="48"
                    strokeLinecap="round" strokeLinejoin="round">
                    <path d="M112 244l144-144 144 144" />
                    <path d="M256 120v292" />
                </svg>
            </button>
        </>
    );
};

export default ProductCategoryList;